import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import {
  RefreshCw, TrendingUp, Users, DollarSign, Target,
  Ticket, ClipboardList, Award, BarChart, AlertTriangle,
  Shield, Megaphone, Heart, Truck, ArrowRight, ArrowUpRight,
  CheckCircle2, Clock, Gauge, Zap, Eye
} from 'lucide-react';
import { cn } from '../lib/utils';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';
import { useCurrency } from '../contexts/CurrencyContext';

const API = process.env.REACT_APP_BACKEND_URL;

const KPICard = ({ label, value, sub, icon: Icon, color, accent, onClick }) => (
  <Card
    className={cn(
      "bg-white border-gray-border hover:shadow-md transition-all duration-200",
      onClick && "cursor-pointer"
    )}
    onClick={onClick}
    data-testid={`cc-kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}
  >
    <CardContent className="p-4">
      <div className="flex items-start justify-between mb-2">
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center"
          style={{ backgroundColor: `${color}15` }}
        >
          <Icon className="h-5 w-5" style={{ color }} />
        </div>
        {accent && (
          <Badge
            className={cn(
              "text-[10px] px-1.5",
              accent === 'danger' ? 'bg-red-100 text-red-700' :
              accent === 'warning' ? 'bg-amber-100 text-amber-700' :
              'bg-emerald-100 text-emerald-700'
            )}
          >
            {sub}
          </Badge>
        )}
      </div>
      <p className="text-2xl font-bold text-text-primary">{value}</p>
      <p className="text-[11px] text-gray-medium mt-0.5">{label}</p>
      {!accent && sub && <p className="text-[10px] text-gray-400 mt-0.5">{sub}</p>}
    </CardContent>
  </Card>
);

const ModuleCard = ({ name, count, color, path, onClick }) => (
  <button
    className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors text-left w-full group"
    onClick={onClick}
    data-testid={`cc-module-${name.toLowerCase().replace(/[\s&]+/g, '-')}`}
  >
    <div
      className="w-2 h-8 rounded-full flex-shrink-0"
      style={{ backgroundColor: color }}
    />
    <div className="flex-1 min-w-0">
      <p className="text-xs font-medium text-text-primary truncate">{name}</p>
      <p className="text-lg font-bold text-text-primary">{count}</p>
    </div>
    <ArrowRight className="h-4 w-4 text-gray-300 group-hover:text-gray-500 transition-colors" />
  </button>
);

const QuickLink = ({ label, path, icon: Icon, color, onClick }) => (
  <button
    className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg border border-gray-200 hover:border-gray-300 hover:shadow-sm transition-all text-left group"
    onClick={onClick}
    data-testid={`cc-quick-${label.toLowerCase().replace(/\s+/g, '-')}`}
  >
    <div
      className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
      style={{ backgroundColor: `${color}15` }}
    >
      <Icon className="h-3.5 w-3.5" style={{ color }} />
    </div>
    <span className="text-xs font-medium text-gray-700 group-hover:text-text-primary truncate">{label}</span>
    <ArrowUpRight className="h-3 w-3 text-gray-300 ml-auto flex-shrink-0" />
  </button>
);

export default function CommandCenterPage() {
  const { format: fc } = useCurrency();
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);
  const navigate = useNavigate();

  const fetchMetrics = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/command-center/metrics`);
      setMetrics(data);
      setLastRefresh(new Date());
    } catch (err) {
      console.error('Failed to fetch command center metrics:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  // Smart polling — lightweight check every 5s, full fetch only on change
  useEffect(() => {
    if (!lastRefresh) return;
    let since = lastRefresh.toISOString();
    const check = async () => {
      try {
        const { data } = await axios.get(
          `${API}/api/command-center/has-updates?since=${encodeURIComponent(since)}`,
          
        );
        if (data.has_updates) {
          since = data.last_change || new Date().toISOString();
          fetchMetrics(true);
        }
      } catch { /* silent */ }
    };
    const interval = setInterval(check, 5000);
    return () => clearInterval(interval);
  }, [lastRefresh, fetchMetrics]);

  const k = metrics?.kpis || {};

  if (loading && !metrics) {
    return (
      <div className="space-y-6 animate-fadeIn" data-testid="command-center-loading">
        <div className="h-8 w-48 bg-gray-200 rounded animate-pulse" />
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-28 bg-gray-100 rounded-xl animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="command-center">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
            <Gauge className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-heading font-bold text-text-primary" data-testid="command-center-title">
              Command Center<PageInfoTooltip pageKey="command-center" />
            </h1>
            <p className="text-[11px] text-gray-medium flex items-center gap-1.5">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
              </span>
              <span className="text-emerald-600 font-medium text-[10px]">LIVE</span>
              <span className="text-gray-300 mx-0.5">|</span>
              {lastRefresh
                ? `Updated ${lastRefresh.toLocaleTimeString()}`
                : 'Loading...'}
            </p>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => fetchMetrics(true)}
          disabled={refreshing}
          data-testid="cc-refresh-btn"
        >
          <RefreshCw className={cn("h-3.5 w-3.5 mr-1.5", refreshing && "animate-spin")} />
          Refresh
        </Button>
      </div>

      {/* Primary KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <KPICard
          label="Total Revenue"
          value={fc(k.total_revenue || 0)}
          sub={`${k.pending_invoices || 0} pending`}
          icon={DollarSign}
          color="#10B981"
          onClick={() => navigate('/sales/invoices')}
        />
        <KPICard
          label="Total Leads"
          value={k.total_leads || 0}
          sub={`+${k.new_leads_30d || 0} this month`}
          icon={Users}
          color="#3B82F6"
          onClick={() => navigate('/leads/pipeline')}
        />
        <KPICard
          label="NPS Score"
          value={k.avg_nps || 0}
          sub={`${k.total_nps_responses || 0} responses`}
          icon={BarChart}
          color="#EC4899"
          accent={k.avg_nps >= 50 ? 'success' : k.avg_nps >= 0 ? 'warning' : 'danger'}
          onClick={() => navigate('/retention/nps')}
        />
        <KPICard
          label="Open Tickets"
          value={k.open_tickets || 0}
          sub={`${k.resolved_tickets || 0} resolved`}
          icon={Ticket}
          color="#14B8A6"
          onClick={() => navigate('/service/helpdesk')}
        />
        <KPICard
          label="Churn Risk"
          value={k.high_churn_risk || 0}
          sub={`${k.high_churn_risk || 0} high risk`}
          icon={AlertTriangle}
          color="#EF4444"
          accent={k.high_churn_risk > 5 ? 'danger' : k.high_churn_risk > 0 ? 'warning' : 'success'}
          onClick={() => navigate('/retention/churn-prediction')}
        />
        <KPICard
          label="SLA Compliance"
          value={`${k.sla_compliance || 100}%`}
          sub={`${k.sla_total || 0} tracked`}
          icon={Shield}
          color="#6366F1"
          accent={k.sla_compliance >= 95 ? 'success' : k.sla_compliance >= 80 ? 'warning' : 'danger'}
          onClick={() => navigate('/service/sla')}
        />
      </div>

      {/* Secondary KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPICard
          label="Task Completion"
          value={`${k.task_completion_rate || 0}%`}
          sub={`${k.completed_tasks || 0}/${k.total_tasks || 0} tasks`}
          icon={ClipboardList}
          color="#F59E0B"
          onClick={() => navigate('/operations/tasks')}
        />
        <KPICard
          label="Goals Progress"
          value={`${k.goals_completed || 0}/${k.goals_total || 0}`}
          sub="completed"
          icon={Target}
          color="#10B981"
          onClick={() => navigate('/analytics/goals')}
        />
        <KPICard
          label="Active Campaigns"
          value={k.active_campaigns || 0}
          sub="email + push"
          icon={Megaphone}
          color="#F97316"
          onClick={() => navigate('/email')}
        />
        <KPICard
          label="Loyalty Members"
          value={k.loyalty_members || 0}
          sub={`${k.total_employees || 0} employees`}
          icon={Award}
          color="#8B5CF6"
          onClick={() => navigate('/retention/loyalty')}
        />
      </div>

      {/* Module Activity Grid + Quick Links */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Module Activity */}
        <Card className="lg:col-span-2 bg-white border-gray-border" data-testid="cc-module-activity">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-sm font-semibold text-text-primary">Module Activity</h2>
                <p className="text-[10px] text-gray-400">Records across all modules</p>
              </div>
              <Badge variant="outline" className="text-[10px]">
                {(metrics?.modules || []).reduce((sum, m) => sum + m.count, 0)} total records
              </Badge>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
              {(metrics?.modules || []).map((mod, i) => (
                <ModuleCard
                  key={i}
                  name={mod.name}
                  count={mod.count}
                  color={mod.color}
                  path={mod.path}
                  onClick={() => navigate(mod.path)}
                />
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Quick Links */}
        <Card className="bg-white border-gray-border" data-testid="cc-quick-links">
          <CardContent className="p-4">
            <h2 className="text-sm font-semibold text-text-primary mb-3">Quick Navigation</h2>
            <div className="space-y-1.5">
              <QuickLink label="Management Hub" path="/management-hub" icon={Eye} color="#06B6D4" onClick={() => navigate('/management-hub')} />
              <QuickLink label="Sales Pipeline" path="/sales/pipeline" icon={TrendingUp} color="#8B5CF6" onClick={() => navigate('/sales/pipeline')} />
              <QuickLink label="Churn Prediction" path="/retention/churn-prediction" icon={AlertTriangle} color="#EF4444" onClick={() => navigate('/retention/churn-prediction')} />
              <QuickLink label="Goal Tracking" path="/analytics/goals" icon={Target} color="#10B981" onClick={() => navigate('/analytics/goals')} />
              <QuickLink label="Campaign Calendar" path="/marketing/campaign-calendar" icon={Megaphone} color="#F97316" onClick={() => navigate('/marketing/campaign-calendar')} />
              <QuickLink label="Dispatch Board" path="/operations/dispatch" icon={Truck} color="#F59E0B" onClick={() => navigate('/operations/dispatch')} />
              <QuickLink label="Invoice Generator" path="/sales/invoices" icon={DollarSign} color="#10B981" onClick={() => navigate('/sales/invoices')} />
              <QuickLink label="Audit Log" path="/settings/audit-log" icon={Shield} color="#6B7280" onClick={() => navigate('/settings/audit-log')} />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* System Health Bar */}
      <Card className="bg-white border-gray-border" data-testid="cc-system-health">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-text-primary">System Health</h2>
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              <span className="text-[10px] text-emerald-600 font-medium">All Systems Operational</span>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { label: 'SLA Health', value: k.sla_compliance || 100, color: '#6366F1' },
              { label: 'Task Completion', value: k.task_completion_rate || 0, color: '#F59E0B' },
              {
                label: 'Goal Progress',
                value: k.goals_total ? Math.round((k.goals_completed / k.goals_total) * 100) : 0,
                color: '#10B981',
              },
              {
                label: 'Ticket Resolution',
                value: (k.open_tickets + k.resolved_tickets) > 0
                  ? Math.round((k.resolved_tickets / (k.open_tickets + k.resolved_tickets)) * 100)
                  : 100,
                color: '#14B8A6',
              },
            ].map((item, i) => (
              <div key={i} className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-gray-500">{item.label}</span>
                  <span className="text-[10px] font-medium" style={{ color: item.color }}>{item.value}%</span>
                </div>
                <Progress value={item.value} className="h-1.5" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
